import React, { useImperativeHandle, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { v4 as uuid } from "uuid";
import { isFunction, refIsRequiredError } from "@util/utils";

/**
 * 判斷字串中是否包含星號（視為加密值）
 * @param {string} value - 欲判斷的字串
 * @returns {boolean} 是否包含 *
 */
const hasAsterisk = value => /\*+/gm.test(value || "");

/**
 * `ProtectedInputField` 是一個用於處理加密輸入的通用元件，例如：顯示 `****5678` 的信用卡或身份證字號欄位。
 * - 初始值中若包含星號（*），將不強制驗證 required，直到使用者開始輸入為止。
 * - 提供 `getResult()`、`isError()` 等 imperative 方法給父元件使用。
 *
 * @typedef {Object} ProtectedInputFieldProps
 * @property {React.ElementType} as - 欲渲染的元件（例如 `<Input />`、`<TextField />` 等）
 * @property {string} [defaultValue] - 初始顯示值，可包含加密文字（例如：****1234）
 * @property {string} [value] - 受控元件的值（與 defaultValue 二選一）
 * @property {string} [placeholder] - 輸入框的提示文字
 * @property {(event: React.ChangeEvent<HTMLInputElement>, ...args: any[]) => void} [onChange] - 使用者輸入時的回呼函式
 * @property {boolean} [isError] - 是否顯示錯誤樣式
 * @property {boolean} [required] - 是否為必填欄位
 *
 * @component
 * @example
 * <ProtectedInputField
 *   as={CustomInput}
 *   defaultValue="****1234"
 *   required
 *   onChange={(e) => console.log(e.target.value)}
 *   placeholder="請輸入信用卡號"
 */
const ProtectedInputField = React.forwardRef(({ defaultValue, value, placeholder: placeholderProp, as: Component, onChange, isError: isErrorProp = false, required: requiredProps, ...rest }, ref) => {
	const filedRef = useRef(null);
	// 已編輯參考
	const hasBeenEditedRef = useRef(false);

	// 判斷是否為加密模式（預設值或 value 中含有 *）
	const isEncrypted = useMemo(
		() => hasAsterisk(defaultValue || value),
		// eslint-disable-next-line
		[defaultValue, value]
	);

	// 判斷是否為非加密 & 使用者還沒編輯 → 不送 required
	const shouldRequire = useMemo(
		() => {
			return !isEncrypted && requiredProps;
		},
		// eslint-disable-next-line
		[isEncrypted, requiredProps]
	);

	// 加密狀態下的 placeholder
	const inputPlaceholder = useMemo(
		() => {
			return isEncrypted && !hasBeenEditedRef.current ? defaultValue || value || placeholderProp : placeholderProp;
		},
		// eslint-disable-next-line
		[defaultValue, value, placeholderProp, isEncrypted]
	);

	// 根據模式選擇應傳入 <Component /> 的值：defaultValue / value / placeholder
	const fieldValueProps = useMemo(
		() => {
			if (isEncrypted && !hasBeenEditedRef.current) {
				return {
					inputProps: { placeholder: inputPlaceholder },
				};
			}

			if (defaultValue !== undefined && value === undefined) {
				// 非受控元件：使用 defaultValue
				return {
					defaultValue,
					key: uuid(),
				};
			}

			if (value !== undefined && defaultValue === undefined) {
				// 受控元件：使用 value
				return {
					value,
				};
			}
			return {};
		},
		// eslint-disable-next-line
		[defaultValue, value, inputPlaceholder, isEncrypted]
	);

	const handleChange = useCallback(
		(e, ...rest) => {
			if (!hasBeenEditedRef.current) {
				hasBeenEditedRef.current = true;
			}
			if (isFunction(onChange)) onChange(e, ...rest);
		},
		// eslint-disable-next-line
		[onChange]
	);

	useImperativeHandle(
		ref,
		() => ({
			isError: () => {
				if (isEncrypted) return false;
				return refIsRequiredError(filedRef);
			},
			getResult: () => {
				if (hasBeenEditedRef.current) {
					return filedRef.current?.getResult();
				}
				return defaultValue || value || "";
			},
			getName: () => filedRef.current.name,
			getId: () => filedRef.current.id,
			node: () => filedRef.current,
		}),
		// eslint-disable-next-line
		[defaultValue, value, isEncrypted]
	);

	return <Component {...rest} ref={filedRef} isError={isErrorProp} required={shouldRequire} onChange={handleChange} {...fieldValueProps} />;
});

ProtectedInputField.propTypes = {
	as: PropTypes.elementType.isRequired,
	defaultValue: PropTypes.string,
	placeholder: PropTypes.string,
	onChange: PropTypes.func,
	isError: PropTypes.bool,
	required: PropTypes.bool,
};

export default ProtectedInputField;
